import { type ReactElement, useEffect, useState } from 'react';
import { Box, Icon, Modal, Spinner, Text, Title } from '@nimbus-ds/components';
import { MarketingIcon, RocketIcon, StarIcon } from '@nimbus-ds/icons';
import type { PromoteGroupNameType } from '@tiendanube/common';
import { IconBox, useResponsive } from 'commons/components';
import { PandaPlayerEmbed } from 'commons/components/PandaPlayerEmbed';
import { useTranslationDashboard } from 'domains/Dashboard/hooks';
import './PromoteVideoModal.scss';

const PROMOTE_GROUP_ICON: Record<PromoteGroupNameType, ReactElement> = {
  generate_confidence: <StarIcon />,
  increase_traffic: <MarketingIcon />,
  improve_conversion: <RocketIcon />,
};

type PromoteVideoModalProps = Readonly<{
  open: boolean;
  title: string;
  groupName?: PromoteGroupNameType;
  videoId?: string;
  onDismiss: () => void;
}>;

export function PromoteVideoModal({
  open,
  title,
  groupName,
  videoId,
  onDismiss,
}: PromoteVideoModalProps) {
  const t = useTranslationDashboard();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isMobile } = useResponsive();

  // Reset loading when modal opens
  useEffect(() => {
    if (open) {
      setIsLoading(true);
      setError(null);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onDismiss={onDismiss}
      maxWidth="655px"
      className="stratus--promote-video-modal"
      data-style="stratus--promote-video-modal"
    >
      <Modal.Header>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="flex-start"
          gap="2"
          padding="none"
        >
          <IconBox
            backgroundColor="primary-surface"
            color="primary-textLow"
            borderRadius="2"
            height="28px"
            width="28px"
          >
            {groupName ? (
              <Icon
                source={PROMOTE_GROUP_ICON[groupName]}
                color="currentColor"
              />
            ) : null}
          </IconBox>
          <Title as="h4" fontWeight="bold">
            {title}
          </Title>
        </Box>
      </Modal.Header>
      <Modal.Body padding="none">
        <Box
          position="relative"
          minHeight={isMobile ? '160px' : '350px'}
          padding="none"
        >
          {videoId ? (
            <>
              {isLoading && error === null && (
                <Box
                  position="absolute"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  backgroundColor="neutral-surface"
                >
                  <Spinner size="large" />
                </Box>
              )}
              {error !== null && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                  padding="4"
                >
                  <Text color="danger-textHigh">
                    {t('promote.video.modal.load_failed')}
                  </Text>
                </Box>
              )}
              {error === null && (
                <PandaPlayerEmbed
                  videoId={videoId}
                  autoPlay
                  onReady={() => setIsLoading(false)}
                  onError={() => {
                    setIsLoading(false);
                    setError('load_error');
                  }}
                />
              )}
            </>
          ) : (
            <Text>{t('promote.video.modal.not_found')}</Text>
          )}
        </Box>
      </Modal.Body>
    </Modal>
  );
}
